// Pipeline: a greeting banner with the headline numbers, then one tree of
// the usage projects and their open action items, open by default, under
// two sections of project cards, listing only the items assigned to the user. "Next
// moves" holds the projects with one; "Waiting" the ones with nothing
// on the user, waiting on InfinityLoop or on someone else. A switch shows the
// same projects as a board, a column per stage. Only the Microsoft EA project
// drills into its project page.

import { FC, useMemo } from "react";
import { useUserState } from "../../../../common/UserContext";
import { VendorPage } from "../../../vendor/VendorPage";
import { Banner, BannerCount } from "../../../negotiations/v2/list/components/Banner";
import {
    closedProjects,
    dealById,
    effectiveProjects,
    nextMoveCount,
    pipelineSections,
    unusedOf,
    realizedSavings,
} from "../model/derived";
import { isDrillableProject } from "../model/fixtures";
import { moneyShort, pct } from "../model/format";
import { useUsageDemoState } from "../model/store";
import type { UsageProject } from "../model/types";
import { PipelineBoard } from "./PipelineBoard";

const isDrillable = (project: UsageProject) => isDrillableProject(project.id);

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const PipelineView: FC = () => {
    const { me } = useUserState();
    const state = useUsageDemoState();
    const projects = useMemo(() => effectiveProjects(state), [state]);
    const { nextMoves, waiting } = useMemo(() => {
        const sections = pipelineSections(projects);
        // The project the demo drills into leads its section.
        const drillableFirst = (projects: UsageProject[]) => [
            ...projects.filter(isDrillable),
            ...projects.filter((p) => !isDrillable(p)),
        ];
        return {
            nextMoves: drillableFirst(sections.nextMoves),
            waiting: drillableFirst(sections.waiting),
        };
    }, [projects]);
    const finished = closedProjects(projects);
    // The headline numbers are the listed projects' own, so they add up to the table.
    const dealOf = (p: UsageProject) => dealById(p.dealId)!;
    const open = [...nextMoves, ...waiting];
    // The same count as the nav entry's badge: items, not projects.
    const moves = nextMoveCount(nextMoves);
    const targetSavings = open.reduce((sum, p) => sum + p.estSavings, 0);
    const spend = open.reduce((sum, p) => sum + dealOf(p).annualCost, 0);
    const unused = open.reduce((sum, p) => sum + unusedOf(dealOf(p)), 0);

    return (
        <VendorPage>
            <Banner
                name={me.firstName}
                message={
                    <>
                        you have{" "}
                        <BannerCount>
                            {plural(open.length, "active project", "active projects")}
                        </BannerCount>{" "}
                        and{" "}
                        <BannerCount color="orange">
                            {plural(moves, "action item", "action items")}
                        </BannerCount>{" "}
                        to catch up on.
                    </>
                }
                metrics={[
                    {
                        label: "Spend",
                        value: moneyShort(spend),
                        detail: `across ${plural(open.length, "open project", "open projects")}`,
                    },
                    {
                        label: "Paying for unused capacity",
                        value: moneyShort(unused),
                        color: "red",
                        detail: `${pct((unused / spend) * 100)} of that spend`,
                    },
                    {
                        label: "Target savings",
                        value: moneyShort(targetSavings),
                        color: "green",
                        detail: `${pct((targetSavings / spend) * 100)} of that spend`,
                    },
                    {
                        label: "Realized savings",
                        value: moneyShort(realizedSavings(projects)),
                        color: "green",
                        detail: `${plural(finished.length, "project", "projects")} closed this year`,
                    },
                ]}
            />
            <PipelineBoard projects={[...open, ...finished]} />
        </VendorPage>
    );
};
